import {mxResources} from '../mxGraphFactory';
import isEmpty from 'lodash/isEmpty';
import GraphParallelJoinResolver from './GraphParallelJoinResolver';

const UNVISITED = -1;
export default class GraphStructureValidator {
    constructor(name, startTask, cell, model) {
        this.model = model;
        this.name = name;
        this.steps = [];
        this.routes = [];
        this.startTask = startTask;

        this.seen = [];
        this.visitOrdinal = 0;

        this.collectCells(cell)
    }

    collectCells(cell) {
        if (cell.isVertex()) {
            this.steps.push(cell);
        } else if (cell.isEdge()) {
            this.routes.push(cell);
        }

        const childCount = cell.getChildCount();
        for (let i = 0; i < childCount; i++) {
            this.collectCells(cell.getChildAt(i));
        }
    }

    startTaskIndex() {
        return this.steps.findIndex(step => step.id === this.startTask);
    }

    validate(validationContext) {
        const actualStepCount = this.steps.length;
        const noTasksError = {
            message: mxResources.get('graphNoTasks', [this.name]),
            graphName: this.name
        };

        if (actualStepCount <= 0) {
            validationContext.addError(noTasksError);
            return;
        }

        const startIndex = this.startTaskIndex();
        if (startIndex === -1) {
            validationContext.addError({
                message: mxResources.get('graphStartTaskMustExist'),
                graphName: this.name
            });
            return;
        }

        if (!this.validateRoutes(validationContext)) {
            return;
        }

        if (!this.canReachAllSteps(actualStepCount, startIndex)) {
            for (let i = 0; i < actualStepCount; ++i) {
                if (this.seen[i] === UNVISITED && !this.isPassThroughStartStep(this.steps[i])) {
                    validationContext.addStepError({
                        message: mxResources.get('taskNotReachableFromStart'),
                        graphName: this.name,
                        stepId: this.steps[i].id,
                        stepName: this.steps[i].value.name
                    });
                }
            }
            return;
        }

        this.validateUniqueStepNames(validationContext);
        new GraphParallelJoinResolver(this.name, this.steps, this.routes, this.model)
            .resolve(startIndex, validationContext);
    }

    validateRoutes(validationContext) {
        let validRoutes = true;

        this.routes.forEach(route => {
            if (!this.isKnownStep(route.source) || !this.isKnownStep(route.target)) {
                validRoutes = false;
                validationContext.addRouteError({
                    message: mxResources.get('routeMustConnectTasks'),
                    graphName: this.name,
                    routeId: route.id,
                    routeName: route.value?.name
                });
            }
        });

        return validRoutes;
    }

    canReachAllSteps(numberOfSteps, startIndex) {
        this.seen = new Array(numberOfSteps).fill(UNVISITED);
        this.visitOrdinal = 0;
        this.visit(startIndex);

        return this.seen.every((stepOrdinal, index) =>
            stepOrdinal !== UNVISITED || this.isPassThroughStartStep(this.steps[index]));
    }

    validateUniqueStepNames(validationContext) {
        const stepNames = new Set();

        this.steps.forEach(step => {
            if (this.isPassThroughStartStep(step)) {
                return;
            }

            const {id, value: {name}} = step;

            if (!name || isEmpty(name)) {
                validationContext.addStepError({
                    fields: {
                        name: mxResources.get('taskNameMustNotBlank')
                    },
                    graphName: this.name,
                    stepId: id,
                    stepName: name
                });
            } else if (stepNames.has(name)) {
                validationContext.addStepError({
                    fields: {
                        name: mxResources.get('taskNameMustBeUnique')
                    },
                    graphName: this.name,
                    stepId: id,
                    stepName: name
                });
            } else {
                stepNames.add(name)
            }
        });
    }

    isPassThroughStartStep(step) {
        const stepValue = step?.value;
        const outgoingRoutes = this.model.getEdges(step, false, true, true);
        return step.isStart?.() &&
            !stepValue?.type &&
            isEmpty(stepValue?.decisionTask?.name) &&
            isEmpty(stepValue?.parallelTask?.name) &&
            stepValue?.splitType !== 'And' &&
            outgoingRoutes.length === 1 &&
            !outgoingRoutes[0].isConditional?.() &&
            this.isKnownStep(outgoingRoutes[0].target);
    }

    visit(theStepIndex) {
        this.seen[theStepIndex] = ++this.visitOrdinal;

        this.model.getEdges(this.steps[theStepIndex], false, true, true).forEach(route => {
            if (!route.target) {
                return;
            }

            const adjacentStepIndex = this.getIndexOfStepId(route.target.id);
            if (adjacentStepIndex !== -1 && this.seen[adjacentStepIndex] === UNVISITED) {
                this.visit(adjacentStepIndex);
            }
        })
    }

    getIndexOfStepId(stepId) {
        return this.steps.findIndex(step => step.id === stepId);
    }

    isKnownStep(step) {
        return step && this.getIndexOfStepId(step.id) !== -1;
    }
}
